<template>
  <div>
    <core-modal-header>Create Merged Video</core-modal-header>

    <core-modal-body>
      <u-alert
        v-if="isInvalidSelection"
        color="blue"
        variant="soft"
        :icon="COMMON_ICONS.info"
        title="Add More Videos"
        description="Select at least two videos to create a merged video."
      />

      <template v-else>
        <u-alert
          v-if="isDurationExceeded"
          color="red"
          :icon="COMMON_ICONS.alert"
          title="Video Too Long"
          :description="`Merged videos can currently be up to ${formatDuration(VIDEO_MERGE_MAX_DURATION_MS)} minutes long. Your selected clips total ${formatDuration(totalDuration, false, true)}. Please remove some clips below to continue.`"
          class="mb-3"
        />

        <div class="flex justify-between items-center text-sm text-gray-500 mb-2">
          <span>Drag to reorder • {{ count }} clips</span>
          <span>Duration: {{ formatDuration(totalDuration, false, true) }}</span>
        </div>

        <div ref="scrollContainerRef" class="overflow-y-auto max-h-104 p-3 border border--charcoal-200/75 rounded-lg mb-4 bg-charcoal-50/50 scrollbar-light">
          <TransitionGroup
            tag="ul"
            name="merge-list"
            class="flex flex-col"
            @leave="onLeave"
          >
          <li
            v-for="item in displayItems"
            :key="item.key"
            :ref="el => { if (el) listItemRefs[item.key] = el; else delete listItemRefs[item.key]; }"
            :draggable="false"
            class="mb-2 last:mb-0"
            @dragstart="onDragStart($event, item)"
            @dragover.prevent="onItemDragOver($event, item)"
            @dragend="onDragEnd"
          >
            <div
              class="flex items-center gap-3 border border-gray-200 rounded p-2 bg-white cursor-grab transition-opacity select-none touch-none"
              :class="{'opacity-0': item.transparent}"
              @pointerdown="onPointerDown($event, item)"
            >
              <u-icon name="i-ri-draggable" class="text-gray-400 w-5 h-5 flex-shrink-0" />

              <div class="relative w-24 h-14 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                <img v-if="item.file.images?.preview" :src="item.file.images.preview" draggable="false" class="w-full h-full object-cover" />
                <div class="absolute bottom-0 left-0 px-1.5 py-0.5 flex items-center gap-0.5 bg-white/60 text-charcoal rounded-tr-md">
                  <u-icon name="i-ri-play-fill" class="w-3 h-3" />
                  <span class="text-xs font-semibold leading-none">{{ formatDuration(item.file.duration) }}</span>
                </div>
              </div>

              <span class="flex-1 text-sm font-medium text-gray-700 truncate">{{ item.file.name }}</span>

              <u-tooltip v-if="orderedFiles.length > 2" text="Remove Video">
                <u-button
                  class="w-9 h-9 flex-shrink-0 mr-1 rounded-full flex-center"
                  color="gray"
                  variant="soft"
                  @click.stop="removeVideo(item.originalIndex)"
                  @pointerdown.stop
                  @dragstart.stop.prevent
                >
                  <u-icon :name="COMMON_ICONS.close" class="text-base  text-gray-950" />
                </u-button>
              </u-tooltip>
            </div>
          </li>
        </TransitionGroup>
        </div>

        <u-form ref="formRef" :state="formState" :schema="schema" @submit="confirm" :validate-on="['submit']" class="border border-charcoal-100 rounded-lg p-4">

          <u-form-group help="Creates short crossfades between adjacent clips." :ui="{help: 'ml-7 mt-0'}">
            <u-checkbox v-model="fadeEnabled" label="Smooth Fade Between Clips" />
          </u-form-group>

          <u-form-group label="New Video Name" name="outputName" required class="mt-3" :ui="{label: {base: 'text-sm'}}">
            <u-input v-model="formState.outputName" class="w-full" />
          </u-form-group>
        </u-form>
      </template>
    </core-modal-body>

    <core-modal-footer>
      <template v-if="isInvalidSelection">
        <u-button size="xl" @click="close">Got It</u-button>
      </template>
      <template v-else>
        <u-button size="xl" color="charcoal" variant="soft" @click="close">Cancel</u-button>
        <u-button size="xl" :loading="isMerging" :disabled="isDurationExceeded" @click="formRef?.submit()">Create Video</u-button>
      </template>
    </core-modal-footer>
  </div>

  <!-- Custom drag preview — follows cursor, transitions to 0.8 scale -->
  <Teleport to="body">
    <div
      v-if="dragPreview.visible"
      class="pointer-events-none fixed z-[9999]"
      :style="{
        left: (dragPreview.x - dragPreview.offsetX) + 'px',
        top: (dragPreview.y - dragPreview.offsetY) + 'px',
        width: dragPreview.width + 'px',
        transform: `scale(${dragPreview.scaled ? 0.9 : 1})`,
        transition: dragPreview.snapping
          ? 'transform 0.2s ease, left 0.2s ease, top 0.2s ease'
          : 'transform 0.2s ease',
        transformOrigin: `${dragPreview.offsetX}px ${dragPreview.offsetY}px`,
      }"
    >
      <div class="flex items-center gap-3 border border-gray-200 rounded-md p-2 bg-white shadow-lg cursor-grabbing">
        <u-icon name="i-ri-draggable" class="text-gray-400 w-5 h-5 flex-shrink-0" />
        <div class="relative w-24 h-14 flex-shrink-0 rounded overflow-hidden bg-gray-100">
          <img v-if="dragPreview.file?.images?.preview" :src="dragPreview.file.images.preview" draggable="false" class="w-full h-full object-cover" />
          <div class="absolute bottom-0 left-0 px-1.5 py-0.5 flex items-center gap-0.5 bg-white/60 text-charcoal rounded-tr-md">
            <u-icon name="i-ri-play-fill" class="w-3 h-3" />
            <span class="text-xs font-semibold leading-none">{{ formatDuration(dragPreview.file?.duration) }}</span>
          </div>
        </div>
        <span class="flex-1 text-sm font-medium text-gray-700 truncate">{{ dragPreview.file?.name }}</span>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
  import {formatDuration} from '~/utils/datetime.js';
  import {object, string} from 'yup';
  import {VIDEO_MERGE_MAX_DURATION_MS} from '~/constants/video-editor.js';

  const props = defineProps({
    files: {
      type: Array,
      default: () => []
    },
    modal: Object,
    contextType: String,
    contextId: String
  });

  const emit = defineEmits(['success']);

  const isMerging = ref(false);
  const fadeEnabled = ref(true);
  const formRef = ref(null);

  function defaultOutputName() {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `Merge ${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  const formState = reactive({ outputName: defaultOutputName() });

  const schema = object({
    outputName: string().required('File name is required.')
  });
  // Processing videos have no duration yet, so they would slip past the duration cap.
  const videoFiles = computed(() => props.files.filter(f => f.is.video && !f.is.errored && !f.is.processing));
  const count = computed(() => orderedFiles.value.length);
  const isInvalidSelection = computed(() => count.value < 2);

  const orderedFiles = ref([]);
  watch(videoFiles, val => { orderedFiles.value = [...val]; }, {immediate: true});

  const totalDuration = computed(() => orderedFiles.value.reduce((sum, f) => sum + (f.duration || 0), 0));
  const isDurationExceeded = computed(() => totalDuration.value > VIDEO_MERGE_MAX_DURATION_MS);

  const dragIndex = ref(null);
  const dropTargetIndex = ref(null);
  const isDragging = ref(false);

  const dragPreview = reactive({
    visible: false,
    scaled: false,
    snapping: false,
    x: 0,
    y: 0,
    offsetX: 0,
    offsetY: 0,
    width: 0,
    file: null,
    position: null
  });

  const listItemRefs = {};
  const droppingKey = ref(null);
  const scrollContainerRef = ref(null);

  let scrollAnimFrame = null;
  let autoScrollSpeed = 0;
  const EDGE_ZONE = 60;
  const MAX_SCROLL_SPEED = 8;
  const DRAGGING_BODY_CLASS = 'video-merge-dragging';
  let activePointerId = null;

  function updateAutoScroll(clientY) {
    if (!scrollContainerRef.value) return;
    const rect = scrollContainerRef.value.getBoundingClientRect();

    if (clientY < rect.top + EDGE_ZONE && clientY >= rect.top) {
      autoScrollSpeed = -Math.ceil((1 - (clientY - rect.top) / EDGE_ZONE) * MAX_SCROLL_SPEED);
    } else if (clientY > rect.bottom - EDGE_ZONE && clientY <= rect.bottom) {
      autoScrollSpeed = Math.ceil((1 - (rect.bottom - clientY) / EDGE_ZONE) * MAX_SCROLL_SPEED);
    } else {
      autoScrollSpeed = 0;
    }

    if (autoScrollSpeed !== 0 && scrollAnimFrame === null) {
      const step = () => {
        if (scrollContainerRef.value && autoScrollSpeed !== 0) {
          scrollContainerRef.value.scrollTop += autoScrollSpeed;
          scrollAnimFrame = requestAnimationFrame(step);
        } else {
          scrollAnimFrame = null;
        }
      };
      scrollAnimFrame = requestAnimationFrame(step);
    } else if (autoScrollSpeed === 0) {
      cancelAutoScroll();
    }
  }

  function cancelAutoScroll() {
    if (scrollAnimFrame !== null) {
      cancelAnimationFrame(scrollAnimFrame);
      scrollAnimFrame = null;
    }
    autoScrollSpeed = 0;
  }

  const displayItems = computed(() => {
    if (!isDragging.value || dragIndex.value === null) {
      return orderedFiles.value.map((f, i) => ({
        type: 'file', file: f, key: f.id, originalIndex: i, displayIndex: i,
        transparent: droppingKey.value === f.id
      }));
    }

    // Build list without the dragged item; track each item's insertion-index
    const withoutDragged = [];
    orderedFiles.value.forEach((f, i) => {
      if (i !== dragIndex.value) {
        withoutDragged.push({
          type: 'file', file: f, key: f.id,
          insertionIndex: withoutDragged.length, // position in withoutDragged space
          transparent: false
        });
      }
    });

    // Insert the dragged item as a transparent placeholder at the drop target
    const draggedFile = orderedFiles.value[dragIndex.value];
    const at = Math.min(dropTargetIndex.value ?? dragIndex.value, withoutDragged.length);
    withoutDragged.splice(at, 0, {
      type: 'file', file: draggedFile, key: draggedFile.id,
      insertionIndex: null,
      transparent: true
    });

    return withoutDragged.map((item, i) => ({...item, displayIndex: i}));
  });

  function updateDragPreviewPosition(event) {
    dragPreview.x = event.clientX;
    dragPreview.y = event.clientY;
    updateAutoScroll(event.clientY);
  }

  function updateDropTargetFromPointer(clientY) {
    if (dragIndex.value === null) return;

    const candidates = displayItems.value
      .filter(item => item.type === 'file' && !item.transparent && item.insertionIndex !== null)
      .map(item => ({
        item,
        el: listItemRefs[item.key]
      }))
      .filter(candidate => candidate.el)
      .sort((a, b) => a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top);

    if (!candidates.length) {
      dropTargetIndex.value = 0;
      dragPreview.position = 1;
      return;
    }

    let nextDropTargetIndex = candidates[candidates.length - 1].item.insertionIndex + 1;

    for (const candidate of candidates) {
      const rect = candidate.el.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) {
        nextDropTargetIndex = candidate.item.insertionIndex;
        break;
      }
    }

    const maxDropTargetIndex = Math.max(orderedFiles.value.length - 1, 0);
    dropTargetIndex.value = Math.min(Math.max(nextDropTargetIndex, 0), maxDropTargetIndex);
    dragPreview.position = dropTargetIndex.value + 1;
  }

  function onPointerDown(event, item) {
    if (item.type !== 'file' || isDragging.value || (event.button !== undefined && event.button !== 0)) return;

    event.preventDefault();
    activePointerId = event.pointerId;

    dragPreview.file = item.file;
    dragPreview.x = event.clientX;
    dragPreview.y = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    dragPreview.offsetX = event.clientX - rect.left;
    dragPreview.offsetY = event.clientY - rect.top;
    dragPreview.width = rect.width;
    dragPreview.visible = true;
    dragPreview.scaled = false;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    document.addEventListener('pointermove', onPointerMove, {passive: false});
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    document.body.classList.add(DRAGGING_BODY_CLASS);

    dragIndex.value = item.originalIndex;
    dropTargetIndex.value = item.originalIndex;
    dragPreview.position = item.originalIndex + 1;
    isDragging.value = true;

    nextTick(() => { dragPreview.scaled = true; });
  }

  function onPointerMove(event) {
    if (!isDragging.value || event.pointerId !== activePointerId) return;

    event.preventDefault();
    updateDragPreviewPosition(event);
    updateDropTargetFromPointer(event.clientY);
  }

  function onPointerUp(event) {
    if (event.pointerId !== activePointerId) return;

    event.preventDefault();
    dropDraggedItem();
  }

  function onPointerCancel(event) {
    if (event.pointerId !== activePointerId) return;

    cancelPointerDrag();
  }

  function teardownPointerDrag() {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerCancel);
    document.body.classList.remove(DRAGGING_BODY_CLASS);
    activePointerId = null;
    cancelAutoScroll();
  }

  function dropDraggedItem() {
    if (dragIndex.value === null || dropTargetIndex.value === null) return;

    // Stop tracking cursor so the preview freezes in place during the snap
    teardownPointerDrag();

    // Capture the placeholder slot's screen position before clearing drag state
    const draggedFile = orderedFiles.value[dragIndex.value];
    const placeholderItem = displayItems.value.find(i => i.transparent);
    const placeholderEl = placeholderItem ? listItemRefs[placeholderItem.key] : null;

    if (placeholderEl) {
      const rect = placeholderEl.getBoundingClientRect();
      dragPreview.snapping = true;
      // Move x/y so that the click-offset point lands exactly on the slot's top-left,
      // making the element flush with the slot (same size → perfectly centered).
      dragPreview.x = rect.left + dragPreview.offsetX;
      dragPreview.y = rect.top + dragPreview.offsetY;
      dragPreview.scaled = false; // animate scale back to 1
    }

    // Compute new order
    const targetIndex = dropTargetIndex.value;
    const next = orderedFiles.value.filter((_, i) => i !== dragIndex.value);
    next.splice(Math.min(targetIndex, next.length), 0, draggedFile);

    // Keep the landing item invisible until the snap animation finishes,
    // then reset all drag state atomically so displayItems computes once cleanly
    droppingKey.value = draggedFile.id;
    isDragging.value = false;
    dragIndex.value = null;
    dropTargetIndex.value = null;
    orderedFiles.value = next;

    setTimeout(() => {
      droppingKey.value = null; // item begins fading in (transition-opacity)
      dragPreview.snapping = false;
      setTimeout(() => { dragPreview.visible = false; }, 160); // hide preview once item is fully visible
    }, 220);
  }

  function cancelPointerDrag() {
    teardownPointerDrag();

    if (isDragging.value) {
      dragPreview.visible = false;
      dragPreview.scaled = false;
      dragPreview.snapping = false;
      isDragging.value = false;
      dragIndex.value = null;
      dropTargetIndex.value = null;
    }
  }

  onBeforeUnmount(() => {
    cancelPointerDrag();
  });

  function onLeave(el, done) {
    el.style.height = el.offsetHeight + 'px';
    el.style.overflow = 'hidden';
    el.style.transition = 'height 0.2s ease, opacity 0.15s ease, margin-bottom 0.2s ease';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.height = '0';
        el.style.opacity = '0';
        el.style.marginBottom = '0';
      });
    });
    setTimeout(done, 220);
  }

  function removeVideo(originalIndex) {
    orderedFiles.value = orderedFiles.value.filter((_, i) => i !== originalIndex);
  }

  function close() {
    props.modal.close();
  }

  async function confirm() {
    if (isMerging.value) return;
    try {
      isMerging.value = true;
      const { createVideoMergeJob } = useVideoEditorApi();
      await createVideoMergeJob({
        outputName: formState.outputName,
        fade: { enabled: fadeEnabled.value },
        sourceFiles: orderedFiles.value.map(f => ({ fileId: f.id })),
        contextType: props.contextType,
        contextId: props.contextId
      });
      useInfoToast().add({
        title: 'Merge Started',
        description: 'Your request is processing. We will send you a notification when complete.'
      });
      emit('success');
      close();
    } catch (e) {
      useErrorToast().add();
    } finally {
      isMerging.value = false;
    }
  }
</script>

<style scoped>
.merge-list-move {
  transition: transform 0.2s ease;
}

:global(body.video-merge-dragging),
:global(body.video-merge-dragging *) {
  cursor: grabbing !important;
}
</style>
